/**
 * /settings — calm sectioned page. Voice & conversation, memory, transcripts,
 * phone preview, data & privacy, legal, log out.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogOut } from "lucide-react";

import AuthNav from "@/components/AuthNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { getMe, getMemory, patchMe, postLogout, deleteMe } from "@/lib/api";

function Section({ title, sub, children, testId }) {
  return (
    <section className="border-b border-border/40 py-6" data-testid={testId}>
      <h2 className="font-serif text-xl text-foreground">{title}</h2>
      {sub && <p className="mt-1 text-sm text-muted-foreground">{sub}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

function Row({ label, hint, children }) {
  return (
    <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
      <div className="sm:max-w-sm">
        <p className="text-sm text-foreground">{label}</p>
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </div>
      {children}
    </div>
  );
}

function Toggle({ value, onChange, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!value}
      onClick={() => onChange(!value)}
      data-testid={testId}
      className={
        "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition " +
        (value ? "bg-[hsl(var(--primary))]" : "bg-muted")
      }
    >
      <span
        className={
          "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition " +
          (value ? "translate-x-5" : "translate-x-0")
        }
      />
    </button>
  );
}

export default function Settings() {
  const navigate = useNavigate();
  const [authReady, setAuthReady] = useState(false);
  const [me, setMe] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [voiceMode, setVoiceMode] = useState("auto");
  const [captions, setCaptions] = useState(false);
  const [memoryConsent, setMemoryConsent] = useState(false);
  const [transcriptStorage, setTranscriptStorage] = useState(false);
  const [endOfDayEmail, setEndOfDayEmail] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const meRes = await getMe();
      if (cancelled) return;
      if (!meRes.body || meRes.body.scope !== "user") {
        navigate("/", { replace: true });
        return;
      }
      setMe(meRes.body);
      setDisplayName(meRes.body.display_name || "");
      // Fields not in /me yet — try optimistic read; fall back to defaults.
      const memRes = await getMemory();
      if (cancelled) return;
      setMemoryConsent(Boolean(memRes.body && memRes.body.memory_consent));
      setAuthReady(true);
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const save = async (patch) => {
    setSaving(true);
    const { ok } = await patchMe(patch);
    setSaving(false);
    return ok;
  };

  const onLogout = async () => {
    await postLogout();
    navigate("/", { replace: true });
  };

  const onDeleteAccount = async () => {
    const phrase = window.prompt("Type DELETE to confirm. This cannot be undone.");
    if (phrase !== "DELETE") return;
    await deleteMe();
    navigate("/", { replace: true });
  };

  if (!authReady) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="settings-page">
      <AuthNav />
      <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        <header className="mb-2">
          <h1 className="font-serif text-3xl tracking-tight" data-testid="settings-heading">
            Settings
          </h1>
        </header>

        <Section title="Profile" testId="settings-profile">
          <Row label="Display name" hint="How Jesus addresses you.">
            <div className="flex items-center gap-2">
              <Input
                data-testid="settings-display-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="w-56"
              />
              <Button
                data-testid="settings-display-name-save"
                onClick={() => save({ display_name: displayName })}
                disabled={saving || !displayName.trim()}
                className="rounded-full"
              >
                Save
              </Button>
            </div>
          </Row>
          <div className="mt-3">
            <Row label="Email" hint="Read-only.">
              <p className="text-sm text-muted-foreground" data-testid="settings-email">{me?.email || "—"}</p>
            </Row>
          </div>
        </Section>

        <Section
          title="Voice & conversation"
          sub="Choose how Jesus speaks back to you, and whether captions appear during calls."
          testId="settings-voice"
        >
          <Row label="Voice mode" hint="Realtime is lower latency; ElevenLabs is warmer.">
            <Select
              value={voiceMode}
              onValueChange={(v) => { setVoiceMode(v); save({ voice_mode_preference: v }); }}
            >
              <SelectTrigger className="w-56" data-testid="settings-voice-mode">
                <SelectValue placeholder="Choose…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto</SelectItem>
                <SelectItem value="openai_realtime">OpenAI Realtime</SelectItem>
                <SelectItem value="elevenlabs">ElevenLabs</SelectItem>
              </SelectContent>
            </Select>
          </Row>
          <div className="mt-4">
            <Row label="Captions on by default">
              <Toggle
                value={captions}
                onChange={(v) => { setCaptions(v); save({ captions_default: v }); }}
                testId="settings-captions"
              />
            </Row>
          </div>
        </Section>

        <Section title="Memory" sub="Manage what Jesus quietly remembers across calls." testId="settings-memory">
          <Row label="Allow memory" hint="When off, nothing is stored or referenced.">
            <Toggle
              value={memoryConsent}
              onChange={(v) => { setMemoryConsent(v); save({ memory_consent: v }); }}
              testId="settings-memory-consent"
            />
          </Row>
          <div className="mt-4">
            <Button variant="outline" onClick={() => navigate("/memory")} className="rounded-full" data-testid="settings-open-memory">
              Open memory →
            </Button>
          </div>
        </Section>

        <Section title="Transcripts" testId="settings-transcripts">
          <Row label="Save transcripts">
            <Toggle
              value={transcriptStorage}
              onChange={(v) => { setTranscriptStorage(v); save({ transcript_storage: v }); }}
              testId="settings-transcripts-toggle"
            />
          </Row>
          <p className="mt-3 italic text-xs text-muted-foreground">
            Transcripts are private and only ever visible to you.
          </p>
        </Section>

        <Section title="Phone (preview)" testId="settings-phone">
          <p className="text-sm text-muted-foreground">
            Coming soon — link a phone number to call Jesus from anywhere.
          </p>
        </Section>

        <Section title="Notifications" testId="settings-notifications">
          <Row label="Send me a quiet thought at sunset" hint="A short reflection email at ~7pm in your timezone.">
            <Toggle
              value={endOfDayEmail}
              onChange={(v) => { setEndOfDayEmail(v); save({ notification_end_of_day_email: v }); }}
              testId="settings-eod-email"
            />
          </Row>
        </Section>

        <Section title="Data & privacy" testId="settings-data">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">Download my data</p>
            <Button variant="outline" disabled className="rounded-full" data-testid="settings-download">
              Coming soon
            </Button>
          </div>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-destructive">Delete my account</p>
            <Button
              variant="outline"
              onClick={onDeleteAccount}
              className="rounded-full text-destructive"
              data-testid="settings-delete-account"
            >
              Delete account
            </Button>
          </div>
        </Section>

        <Section title="Legal & safety" testId="settings-legal">
          <div className="space-y-3 text-sm leading-relaxed text-muted-foreground">
            <p>
              This is an AI companion inspired by the teachings of Jesus. It is not
              literally Jesus, God, divine, a pastor, a therapist, a doctor, a lawyer,
              or an emergency service.
            </p>
            <p>
              Conversations may be incomplete or wrong. Nothing here is medical,
              mental-health, legal, financial, or emergency advice.
            </p>
            <p>
              If you are in crisis or someone is in danger, please contact local
              emergency services, a crisis hotline, or a trusted person immediately.
            </p>
          </div>
        </Section>

        <div className="mt-8 flex justify-end">
          <Button
            variant="ghost"
            onClick={onLogout}
            data-testid="settings-logout"
            className="text-muted-foreground"
          >
            <LogOut className="mr-1.5 h-4 w-4" />
            Log out
          </Button>
        </div>
      </main>
    </div>
  );
}
